import type { DefaultError } from '@/types/httpError'
import type { Shop, ShopShort, ShopsPayload, ShopsResponse } from '@/types/shops'
import type {
  DefaultDeletePayload,
  DeleteSuccessResponse,
} from '@/types/main'
import { getShopsCall, getShopsListCall, deleteShopCall } from '@/api/shops'
import { useApiCall } from '@/composables/useApiCall'
import { defineStore } from 'pinia'
import { computed, reactive, ref } from 'vue'

export const useShopsStore = defineStore('shops', () => {
  const shops = ref<Shop[]>([])
  const shopsList = ref<ShopShort[]>([])
  const metaData = reactive({
    total: 0,
    totalPages: 0,
  })
  const filterFields = reactive({
    page: 1,
    perPage: 15,
  }) as ShopsPayload

  const {
    data: shopsData,
    executeApiCall: shopsAction,
    isLoading: shopsLoading,
    error: shopsError,
  } = useApiCall<ShopsResponse, DefaultError, ShopsPayload>(getShopsCall, true)

  const {
    data: shopsListData,
    executeApiCall: shopsListAction,
    error: shopsListError,
  } = useApiCall<Shop[], DefaultError, ShopsPayload>(getShopsListCall, true)

  const isShopsLoading = computed(() => shopsLoading.value)

  const changePage = async (page: number) => {
    if (filterFields.page === page) {
      return
    }
    filterFields.page = page
    await getShops()
  }

  const clearFilter = async () => {
    clearQuery()
    await applyFilter(filterFields)
  }

  const applyFilter = async (filters: ShopsPayload) => {
    Object.keys(filters).forEach(key => {
      if (filters[key as keyof ShopsPayload] || filters[key as keyof ShopsPayload] === '0') {
        filterFields[key as keyof ShopsPayload] = filters[key as keyof ShopsPayload]
      } else {
        filterFields[key as keyof ShopsPayload] = ''
      }
    })
    filterFields.page = 1
    await getShops()
  }

  const applySort = async (value: string | null) => {
    if (value) {
      filterFields.sort = value
    } else {
      delete filterFields.sort
    }
    filterFields.page = 1
    await getShops()
  }

  const getShops = async () => {
    try {
      const filter = {} as ShopsPayload
      Object.keys(filterFields).forEach(key => {
        if (
          filterFields[key as keyof ShopsPayload]
          || filterFields[key as keyof ShopsPayload] === false
        ) {
          filter[key as keyof ShopsPayload] = filterFields[key as keyof ShopsPayload]
        }
      })
      await shopsAction(filter)
      if (shopsData.value) {
        shops.value = [...shopsData.value.data]
        metaData.total = shopsData.value.pagination.total
        metaData.totalPages = shopsData.value.pagination.totalPages
      }
    } catch {
      console.error(shopsError.value)
    }
  }

  const getShopsList = async (params: Partial<ShopsPayload>, fieldName?: boolean) => {
    try {
      await shopsListAction(params)
      if (shopsListData.value) {
        shopsList.value = [
          ...shopsListData.value.map(shop => ({ id: shop.id, name: fieldName ? shop.name : shop.address, active: shop.active })),
        ]
      }
    } catch {
      console.error(shopsListError.value)
    }
  }

  const clearQuery = () => {
    metaData.totalPages = 0
    metaData.total = 0
    filterFields.page = 1
    filterFields.perPage = 15
    Object.keys(filterFields).forEach(
      n => ['page', 'perPage'].includes(n) || delete filterFields[n],
    )
  }

  const { executeApiCall: deleteShopAction, error: deleteShopError } = useApiCall<
    DeleteSuccessResponse,
    DefaultError,
    DefaultDeletePayload
  >(deleteShopCall, true)

  const deleteShop = async (id: number) => {
    try {
      await deleteShopAction({ id })
    } catch {
      throw deleteShopError.value?.data
    }
  }

  const $reset = () => {
    clearQuery()
    shops.value = []
    shopsList.value = []
  }

  return {
    shops,
    shopsList,
    isShopsLoading,
    metaData,
    filterFields,
    changePage,
    clearQuery,
    applyFilter,
    clearFilter,
    getShops,
    getShopsList,
    applySort,
    deleteShop,
    $reset,
  }
})
